import _                                   from 'underscore';
import React, { FC }                       from "react";
import { Formik }                          from "formik";
import { Form }                            from "semantic-ui-react";
import * as Yup                            from "yup";
import { ProjectQueries }                  from "../../../../queries/ProjectQueries";
import { TextAreaField, TextField }        from "../../../components/forms";
import { UserQueries }                     from "@src/queries";
import { useApolloClient }                 from "@apollo/react-hooks";
import { QueryDialogProps, QueryOpDialog } from "@src/views/components/from-query/QueryOpDialog";
import { BasicDialogContainer }            from "@src/views/components/modals/BasicDialogContainer";
import { Align, Size }                     from "@src/types";
import { useCurrentUser }                  from "@src/hooks";


interface Props extends QueryDialogProps {
    projectId: string;
    projectVersionId: string;
    currentVersionName: string;
    currentVersionDescription: string;
    currentVersionIsActive: boolean;
    onClose?: () => void;
}

interface FormValues {
    name: string;
    description: string;
    sourceProjectVersionId: string;
    isActiveVersion: boolean;
}

const EditProjectVersionDialog: FC<Props> = ( props ) => {

    const {
              projectId,
              projectVersionId,
              currentVersionName,
              currentVersionDescription,
              currentVersionIsActive,
              onVersionUpdated,
              onClose,
              ...queryDialogProps
          } = props;

    const apolloClient    = useApolloClient(),
          { currentUser } = useCurrentUser();

    const currentProject     = currentUser?.projects?.find( p => p.projectId === projectId ),
          projectVersionName = currentVersionName;

    const getProjectVersionName = () => {

        if( !currentProject ) return projectVersionName;


        let existingProjectVersionNames = [ ..._.map( currentProject.project?.projectVersions ?? [], item => item.name ) ],
            sameNames                   = existingProjectVersionNames.filter( name => name.trim() === projectVersionName );

        // Returning the new version name with versioning (e.g., "VersionName v2")
        return `${ projectVersionName } v${ sameNames.length + 1 }`;
    };

    const initialValues: FormValues = {
        name:                   currentVersionName,
        description:            currentVersionDescription,
        isActiveVersion:        currentVersionIsActive,
        sourceProjectVersionId: projectVersionId,
    };

    const validationSchema = Yup.object().shape( {
        name:        Yup.string().test( 'unique name', 'Version names must be unique', ( value: string ) => {
            let existingProjectVersionNames = [ ..._.map( currentProject.project?.projectVersions ?? [], item => item.name ) ];

            return value?.trim() === currentVersionName || !existingProjectVersionNames.includes( value?.trim() ?? '' );
        } ).required( "A version name is required" ),
        description: Yup.string().required( "A version description is required" ),
    } );

    return (
        <Formik<FormValues>
            initialValues={ initialValues }
            validateOnChange={ true }
            validationSchema={ validationSchema }
            onSubmit={ null }>
            { ( formik ) => (
                <QueryOpDialog
                    { ...queryDialogProps }
                    operationDisabled={ !formik.isValid || !formik.dirty }
                    onComplete={ () => {
                        onClose();
                    } }
                    mutation={ ProjectQueries.mutations.UPDATE_PROJECT_VERSION( ProjectQueries.fragments.PROJECT_VERSIONS.EDIT_FIELDS ) }
                    graphArgs={ {
                        variables:           {
                            input: {
                                projectVersionId,
                                name:            formik.values.name,
                                description:     formik.values.description,
                                isActiveVersion: formik.values.isActiveVersion
                            },
                        },
                        awaitRefetchQueries: true,
                        refetchQueries:      [ { query: UserQueries.queries.GET_CURRENT_USER() } ],
                        update:              async ( store: any, response: any ) => {
                            // Update current user with newly evaluated project list
                            let user = await apolloClient.query( { query: UserQueries.queries.GET_CURRENT_USER() } );
                            let updatedData = user.data.me;

                            store.writeQuery( {
                                query: UserQueries.queries.GET_CURRENT_USER(),
                                data:  { me: updatedData },
                            } );
                        },
                    } }
                    submitLabel="Update Version"
                    className={ "no-padding" }
                    alignControls={ Align.CENTER }
                    onSubmit={ () => formik.submitForm() }>

                    <Form>
                        <BasicDialogContainer title={ "Edit Project Version" } roundedTop contentPadding={ [ Size.MD, Size.MD, Size.LG, Size.MD ] }>
                            <TextField name="name" label="Version Name" placeholder="Enter version name"/>
                            <TextAreaField name="description" label="Description" placeholder="Enter version description"/>
                        </BasicDialogContainer>
                    </Form>

                </QueryOpDialog>
            ) }
        </Formik>
    );
};

export { EditProjectVersionDialog };
